// Features: M004-F0712, M004-F0713, M004-F0714. Tests: T-M004-0203, T-M004-0206, T-M004-0207.
//
// Mounted ONCE, in Layout (design 1.6): owns the open/closed state of the single history drawer and the URL query
// `?tx=<key>&org=<id>` (written with `replace` on open; read on load so a link opens it). Opening a second TX replaces
// the first (no stack). Pages never mount their own copy.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TxHistoryContext } from './txHistoryContext';
import TxHistoryDrawer from './TxHistoryDrawer';
import { txIdFromHandOffId, wellFormedTxId } from '../utils/transactionId';

const FULL_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function TxHistoryProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  const invoker = useRef(null);

  const writeQuery = useCallback((next) => {
    const params = new URLSearchParams(location.search);
    if (next) { params.set('tx', next.key); if (next.orgId != null && next.orgId !== '') params.set('org', String(next.orgId)); } else { params.delete('tx'); params.delete('org'); }
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  const openTransaction = useCallback((next) => {
    if (!next || !next.key || next.orgId == null || next.orgId === '') return;
    if (!request) invoker.current = globalThis.document?.activeElement || null;
    const key = FULL_ID.test(next.key) ? next.key.toLowerCase() : (wellFormedTxId(next.key) || next.key);
    const handOffId = next.handOffId && txIdFromHandOffId(next.handOffId) ? next.handOffId.toLowerCase() : (FULL_ID.test(key) ? key : '');
    const normalised = { key, orgId: next.orgId, customerUserId: next.customerUserId, handOffId, action: next.action === 'credit' ? 'credit' : undefined };
    setRequest(normalised);
    writeQuery(normalised);
  }, [request, writeQuery]);

  const closeTransaction = useCallback(() => {
    setRequest(null);
    writeQuery(null);
    const target = invoker.current;
    invoker.current = null;
    if (target && typeof target.focus === 'function') setTimeout(() => target.focus(), 0);
  }, [writeQuery]);

  // A link (or a refresh) with ?tx= opens the drawer; the organisation must be given, never guessed (design 3.1).
  const wantedTx = new URLSearchParams(location.search).get('tx');
  const wantedOrg = new URLSearchParams(location.search).get('org');
  useEffect(() => {
    if (!wantedTx || !wantedOrg) return;
    setRequest((current) => {
      if (current && (current.key === wantedTx || current.handOffId === wantedTx) && String(current.orgId) === wantedOrg) return current;
      const full = FULL_ID.test(wantedTx);
      return { key: full ? wantedTx.toLowerCase() : wantedTx, orgId: wantedOrg, customerUserId: undefined, handOffId: full ? wantedTx.toLowerCase() : '' };
    });
  }, [wantedTx, wantedOrg]);

  // The URL lost ?tx= (Back, or another navigation): close.
  useEffect(() => { if (!wantedTx) { setRequest(null); setBusy(false); } }, [wantedTx]);

  const value = useMemo(() => ({ openTransaction, closeTransaction, current: request, busy }), [openTransaction, closeTransaction, request, busy]);
  return (
    <TxHistoryContext.Provider value={value}>
      {children}
      {request ? <TxHistoryDrawer request={request} onClose={closeTransaction} onOpenOther={openTransaction} onBusyChange={setBusy} /> : null}
    </TxHistoryContext.Provider>
  );
}
